import React, { useEffect, useState } from "react";
import { Workspace } from "polotno/canvas/workspace";
import { storeToVideo } from '@polotno/video-export';
import { getApiBaseUrl } from "../api/utils";

const API_URL = getApiBaseUrl();
const R2_PUBLIC_URL = (process.env.REACT_APP_R2_PUBLIC_URL || "").replace(/\/+$/, "");

function waitForWorkspaceFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.setTimeout(resolve, 120);
      });
    });
  });
}

async function warmMountedPages(store) {
  const pageIds = (store?.pages || []).map((page) => page.id).filter(Boolean);
  store.pages?.forEach((page) => page.set?.({ _forceMount: true, _rendering: true }));
  await waitForWorkspaceFrame();
  await waitForWorkspaceFrame();

  for (const pageId of pageIds) {
    store.selectPage(pageId);
    await store.waitLoading?.();
    await waitForWorkspaceFrame();
  }
  if (pageIds[0]) {
    store.selectPage(pageIds[0]);
    await store.waitLoading?.();
    await waitForWorkspaceFrame();
  }
}

function shouldProxyAudioUrl(src) {
  if (!src || typeof src !== "string" || src.startsWith("data:") || src.startsWith("blob:")) {
    return false;
  }

  if (R2_PUBLIC_URL && src.startsWith(`${R2_PUBLIC_URL}/`)) {
    return true;
  }

  try {
    const parsedUrl = new URL(src);
    return parsedUrl.hostname.endsWith(".r2.dev");
  } catch (_error) {
    return false;
  }
}

function getAudioExportUrl(src) {
  if (!shouldProxyAudioUrl(src)) {
    return src;
  }
  return `${API_URL}/media/r2-proxy?url=${encodeURIComponent(src)}`;
}

async function prepareAudioForExport(store) {
  const originals = [];

  (store?.audios || []).forEach((audio) => {
    const exportSrc = getAudioExportUrl(audio.src);
    if (exportSrc !== audio.src && audio.set) {
      originals.push({ audio, src: audio.src });
      audio.set({ src: exportSrc });
    }
  });

  return {
    restore: () => {
      originals.forEach(({ audio, src }) => audio.set?.({ src }));
    },
  };
}

async function exportStoreToVideoWhenWorkspaceIsReady({ store, fps, pixelRatio, onProgress }) {
  let restoreAudioUrls = () => {};
  try {
    await warmMountedPages(store);
    const audioPreparation = await prepareAudioForExport(store);
    restoreAudioUrls = audioPreparation.restore;
    return await storeToVideo({ store, fps, pixelRatio, onProgress, includeAudio: true });
  } finally {
    restoreAudioUrls();
    store.pages?.forEach((page) => page.set?.({ _forceMount: false, _rendering: false }));
  }
}

export function VideoViewer({ store, templateId, eventId, mode }) {
  const [loading, setLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    if (!store) return;

    setLoading(true);

    const timeout = setTimeout(async () => {
      try {
        // 🌀 Step 1: Export video from Polotno
        // Adjust FPS, duration, etc. as needed
        // const dataUrl = await store.toVideoDataURL({
        //   fps: 30,          // smooth animation
        //   quality: 1,       // highest quality
        //   duration: 5000,   // 5 seconds total
        //   format: "mp4",    // explicitly set to mp4 if supported
        // });

        // // 🧱 Step 2: Convert DataURL → Blob
        // const res = await fetch(dataUrl);
        // const blob = await res.blob();

        const videoBlob = await exportStoreToVideoWhenWorkspaceIsReady({
          store,
          fps: 30, // Frames per second (default: 30)
          pixelRatio: 2, // Pixel ratio for quality (default: 1)
        });

        // 📦 Step 3: Build FormData for upload
        const formData = new FormData();
        formData.append("file", videoBlob, "animation.mp4");
        formData.append("id", mode === "template" ? templateId : eventId);
        formData.append("updateType", mode);
        formData.append("fileType", "video");

        const uploadUrl = `${API_URL}/media/polotno_upload_gif_video`;

        console.log("Uploading to:", uploadUrl);

        // 🚀 Step 4: Upload to FastAPI
        const res1 = await fetch(uploadUrl, { method: "POST", body: formData });
        if (!res1.ok) throw new Error(`Upload failed: ${res1.statusText}`);
        const data = await res1.json();

        // 🎥 Step 5: Display uploaded video
        const uploadedVideoUrl = data.file_url || data.video_url || data.media_url;
        setVideoUrl(uploadedVideoUrl);


      } catch (err) {
        console.error("Video upload error:", err);


      } finally {
        setLoading(false);
      }
    }, 2000);

    return () => clearTimeout(timeout);
  }, [eventId, mode, store, templateId]);

  if (!store) return <div>No store found (try navigating again)</div>;

  const exportWorkspace = (
    <div className="video-export-workspace" aria-hidden="true">
      <Workspace
        store={store}
        pageControlsEnabled={false}
        renderOnlyActivePage={false}
      />
    </div>
  );

  if (loading) {
    return (
      <>
        {exportWorkspace}
        <div className="flex flex-col items-center justify-center h-full">
          <div className="w-48 h-48 bg-gray-200 animate-pulse rounded-lg"></div>
          <p className="mt-4 text-gray-500 italic">Rendering Video preview...</p>
        </div>
      </>
    );
  }

  if (videoUrl) {
    console.log("videoUrl:", videoUrl);
  }

  return (
    <>
      {exportWorkspace}
      <div className="generated-video-shell">
        <video
          src={videoUrl}
          controls
          loop
          className="generated-video-player"
        />
      </div>
    </>
  );
}

